// src/hooks/useModifierValidation.ts
import { useState, useCallback, useMemo } from 'react';
import {
  ModifierGroup,
  AllModifierSelections,
} from '../types/modifier';
import { validateAllModifiers } from '../utils/modifierValidator';

interface UseModifierValidationReturn {
  selections: AllModifierSelections;
  isValid: boolean;
  handleSelectOption: (groupId: string, optionId: string) => void;
  handleQuantityChange: (groupId: string, optionId: string, quantity: number) => void;
  resetSelections: () => void;
  initializeSelections: (initialModifiers: any[]) => void;
  getSelectedCount: (groupId: string) => number;
  isOptionSelected: (groupId: string, optionId: string) => boolean;
  getOptionQuantity: (groupId: string, optionId: string) => number;
}

/**
 * Custom hook for managing modifier selections and validation
 */
export function useModifierValidation(
  modifiers: ModifierGroup[]
): UseModifierValidationReturn {
  const [selections, setSelections] = useState<AllModifierSelections>({});

  // Validate all selections
  const validationResult = useMemo(() => {
    return validateAllModifiers(modifiers, selections);
  }, [modifiers, selections]);

  // Handle option selection (toggle or set quantity to 1)
  const handleSelectOption = useCallback((groupId: string, optionId: string) => {
    setSelections(prev => {
      const group = modifiers.find(m => m.id === groupId);
      if (!group) return prev;

      const groupSelections = prev[groupId] || {};
      const currentSelection = groupSelections[optionId];
      const isCurrentlySelected = currentSelection && currentSelection.quantity > 0;

      // If allowMultiples is false, clear other selections in this group
      if (!group.allowMultiples && !isCurrentlySelected) {
        return {
          ...prev,
          [groupId]: {
            [optionId]: {
              optionId,
              quantity: 1,
            },
          },
        };
      }

      // Toggle selection
      if (isCurrentlySelected) {
        // Deselect
        const newGroupSelections = { ...groupSelections };
        delete newGroupSelections[optionId];
        return {
          ...prev,
          [groupId]: newGroupSelections,
        };
      } else {
        // Select
        return {
          ...prev,
          [groupId]: {
            ...groupSelections,
            [optionId]: {
              optionId,
              quantity: 1,
            },
          },
        };
      }
    });
  }, [modifiers]);

  // Handle quantity change for an option
  const handleQuantityChange = useCallback((
    groupId: string,
    optionId: string,
    quantity: number
  ) => {
    setSelections(prev => {
      if (quantity <= 0) {
        // Remove selection
        const groupSelections = prev[groupId] || {};
        const newGroupSelections = { ...groupSelections };
        delete newGroupSelections[optionId];
        return {
          ...prev,
          [groupId]: newGroupSelections,
        };
      }

      // Update quantity
      return {
        ...prev,
        [groupId]: {
          ...(prev[groupId] || {}),
          [optionId]: {
            optionId,
            quantity,
          },
        },
      };
    });
  }, []);

  // Reset all selections
  const resetSelections = useCallback(() => {
    setSelections({});
  }, []);

  // Initialize selections from previous modifiers (for edit mode)
  const initializeSelections = useCallback((initialModifiers: any[]) => {
    if (!initialModifiers || initialModifiers.length === 0) {
      setSelections({});
      return;
    }

    // Build selections object from initialModifiers array
    // Cart stores: [{ id: groupId, name: groupName, options: [selectedOptions] }]
    const newSelections: AllModifierSelections = {};
    
    initialModifiers.forEach((cartModifier) => {
      // Cart modifier is a GROUP with selected options in cartModifier.options
      const group = modifiers.find(g => g.id === cartModifier.id);
      
      if (group && cartModifier.options && Array.isArray(cartModifier.options)) {
        if (!newSelections[group.id]) {
          newSelections[group.id] = {};
        }
        
        // Loop through selected options in this group
        cartModifier.options.forEach((selectedOption: any) => {
          newSelections[group.id][selectedOption.id] = {
            optionId: selectedOption.id,
            quantity: 1,
          };
        });
      }
    });
    
    setSelections(newSelections);
  }, [modifiers]);

  // Get total selected count for a group
  const getSelectedCount = useCallback((groupId: string): number => {
    const groupSelections = selections[groupId] || {};
    return Object.values(groupSelections).reduce(
      (total, selection) => total + (selection.quantity || 0),
      0
    );
  }, [selections]);

  // Check if an option is selected
  const isOptionSelected = useCallback((groupId: string, optionId: string): boolean => {
    const groupSelections = selections[groupId] || {};
    return !!groupSelections[optionId] && groupSelections[optionId].quantity > 0;
  }, [selections]);

  // Get quantity for an option
  const getOptionQuantity = useCallback((groupId: string, optionId: string): number => {
    const groupSelections = selections[groupId] || {};
    return groupSelections[optionId]?.quantity || 0;
  }, [selections]);

  return {
    selections,
    isValid: validationResult.isValid,
    handleSelectOption,
    handleQuantityChange,
    resetSelections,
    initializeSelections,
    getSelectedCount,
    isOptionSelected,
    getOptionQuantity,
  };
}
